import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Building2, LogOut, Menu } from 'lucide-react';
import { Sidebar } from './Sidebar';
import {
  detectarAmbiente,
  guardarSidebarColapsado,
  leerSidebarColapsado,
  type Ambiente,
} from './layout.helpers';
import { EmpresaSelector } from './EmpresaSelector';
import { useAuthStore } from '@lib/store/auth.store';
import { me } from '@lib/services/auth.service';
import { useMediaQuery } from '@lib/hooks/useMediaQuery';
import { UserRole } from '@lib/types';

// Ambiente y título de la barra superior: ver `layout.helpers.ts` (afuera de
// este archivo porque un módulo de componentes solo puede exportar componentes).
const ENV = detectarAmbiente(window.location.hostname);
const ENV_LABEL: Record<Ambiente, string> = { local: 'Local', test: 'Test', prod: 'Prod' };

export function Layout() {
  const { setEmpresas, setSession, token, user, logout } = useAuthStore();
  const location = useLocation();
  // Menú mobile (drawer). En desktop el sidebar es fijo y este estado no se usa.
  const [menuOpen, setMenuOpen] = useState(false);
  // Cerrar el drawer al cambiar de ruta (navegó desde el menú).
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const meQuery = useQuery({
    queryKey: ['auth', 'me', token],
    queryFn: me,
    enabled: !!token,
  });

  useEffect(() => {
    if (meQuery.data) {
      setEmpresas(meQuery.data.empresas);
      if (token) setSession(token, meQuery.data.user);
    }
  }, [meQuery.data, token, setEmpresas, setSession]);

  // Menú lateral colapsado (solo íconos); se recuerda por navegador. Solo en
  // desktop: en mobile el menú es un drawer y se muestra completo aunque haya
  // quedado colapsado en una pantalla grande.
  const [colapsadoElegido, setColapsado] = useState(leerSidebarColapsado);
  const esDesktop = useMediaQuery('(min-width: 901px)');
  const colapsado = colapsadoElegido && esDesktop;
  function toggleColapsado() {
    setColapsado((c) => {
      guardarSidebarColapsado(!c);
      return !c;
    });
  }

  // Un USUARIO_EMPRESA sin empresa asignada (empresaIds=[]) no debe ver NADA del
  // backoffice (sus vistas saldrían vacías y la lista de padrones filtraba mal).
  // Le mostramos una única pantalla pidiendo que lo asignen. NO aplica a otros
  // roles (TMK/Medicus pueden tener empresaIds=[] legítimamente).
  if (user?.rol === UserRole.USUARIO_EMPRESA && (user.empresaIds?.length ?? 0) === 0) {
    return <SinEmpresaScreen email={user.email} onLogout={logout} />;
  }

  return (
    <div className={colapsado ? 'appShell appShell--colapsado' : 'appShell'}>
      <Sidebar
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        colapsado={colapsado}
        onToggleColapsado={toggleColapsado}
      />
      {menuOpen && (
        <div className="appBackdrop" aria-hidden="true" onClick={() => setMenuOpen(false)} />
      )}
      <main className="appMain">
        <header className="appTopbar">
          <button
            type="button"
            className="appBurger"
            aria-label="Abrir menú"
            onClick={() => setMenuOpen(true)}
          >
            <Menu size={22} />
          </button>
          {/* Sin título acá: cada página ya tiene el suyo y se veía repetido. */}
          <span
            className={`appTopbar__envPill appTopbar__envPill--${ENV}`}
            title={`Ambiente: ${ENV_LABEL[ENV]}`}
          >
            {ENV_LABEL[ENV]}
          </span>
          <span className="appTopbar__spacer" />
          <EmpresaSelector />
        </header>
        <div className="appContent">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

/**
 * Pantalla única para un USUARIO_EMPRESA sin organización asignada: no ve el
 * backoffice, solo el aviso para que el administrador lo asigne a una empresa.
 */
function SinEmpresaScreen({ email, onLogout }: { email?: string; onLogout: () => void }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'var(--color-background, #f5f7fa)',
      }}
    >
      <div
        className="card"
        style={{
          maxWidth: 460,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 14,
          padding: '32px 28px',
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: 'rgba(0,37,100,0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Building2 size={26} color="#002564" />
        </div>
        <h1 className="heading-4" style={{ margin: 0 }}>
          Todavía no tenés una organización asignada
        </h1>
        <p className="text-element-mid" style={{ margin: 0, lineHeight: 1.6 }}>
          Tu usuario está activo pero todavía no está asociado a ninguna empresa. Comunicate con el
          administrador de la página para que te asigne y puedas acceder.
        </p>
        {email && (
          <p className="text-element-mid" style={{ margin: 0, fontSize: 12 }}>
            Ingresaste como <strong>{email}</strong>
          </p>
        )}
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onLogout}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 4 }}
        >
          <LogOut size={15} /> Cerrar sesión
        </button>
      </div>
    </div>
  );
}
